import React, { useEffect, useGlobal, setGlobal, getGlobal, useState, useRef } from "reactn";
import { makeStyles } from "@material-ui/styles";
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ListItem,
  MenuItem,
  Select
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import _ from "lodash";
import * as API from "../../api/api";
import moment from "moment";
import localization from "moment/locale/nl";
import Icon from "../Notify/components/Icons"; // hergebruik hetzelfde icon-component
import clsx from "clsx";

moment.updateLocale("nl", localization);

const useStyles = makeStyles(() => ({
  root: {},
  tableRow: {},
  nameContainer: {},
  textField: { minWidth: 160 }
}));

// helper om codes 10–17 te herkennen
const isKlepstoringCode = (code) => Number.isInteger(code) && code >= 10 && code <= 17;

const Klepstoringen = () => {
  const classes = useStyles();

  const [user] = useGlobal("user");
  const [global] = useGlobal();
  const devices = global?.devices || [];
  const deviceactionlogshash = global?.deviceactionlogshash || [];

  const [layout, setLayout] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hideLayout] = useState(false);

  const [hash, setHash] = useState(() => user?.device_hash ?? "");
  const [today, setToday] = useState(new Date().toISOString().substring(0, 10));
  const [serial, setSerial] = useState("");

  const hasDeviceHash = (value) => devices.some((d) => String(d.device_hash) === String(value));
  const serialOptionsOf = (device) => (device ? Object.values(device.status || {}) : []);
  const hasSerialValue = (options, value) =>
    options.some((s, i) => String(s?.serial ?? i) === String(value));

  const hashChange = (event) => {
    setHash(event.target.value ?? "");
    setSerial("");
  };
  const dayChange = (event) => setToday(event.target.value ?? "");
  const serialChange = (event) => setSerial(event.target.value ?? "");

  const layoutBusyRef = useRef(false);

  const getLayout = async () => {
    try {
      if (layoutBusyRef.current) return;
      layoutBusyRef.current = true;
      if (user?.device_hash) {
        const r = await API.doGetLayout({
          user,
          device_hash: user.device_hash,
          hideLayout
        });
        if (r && Object.keys(r).length > 0) {
          setLayout(r);
        }
      }
    } catch (err) {
      console.log(err?.message || err);
    } finally {
      layoutBusyRef.current = false;
    }
  };

  const getDevicesLogs = async (device_hash, until) => {
    const h = device_hash ?? "";
    const d = (until ?? today).toString().slice(0, 10);
    if (!h || !d) return;
    try {
      await API.getDeviceActionLogHash(h, d);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    API.getDevices();
    getLayout();
    if (hash) getDevicesLogs(hash, today);
  }, []); // mount only

  useEffect(() => {
    const next = user?.device_hash ?? "";
    if (next && next !== hash) {
      setHash(next);
      setSerial("");
      setIsLoading(true);
      getDevicesLogs(next, today);
    }
  }, [user?.device_hash]);

  useEffect(() => {
    if (devices.length > 0 && !hasDeviceHash(hash)) {
      const first = devices[0].device_hash;
      setHash(first);
      setSerial("");
    }
  }, [devices]);

  useEffect(() => {
    if (!hash || !today) return;
    setGlobal({ status: [] });
    setLayout({});
    setIsLoading(true);
    API.getDevices();
    getDevicesLogs(hash, today);
    getLayout();
  }, [hash, today]);

  // filters
  const selectedDevice = devices.find((d) => String(d.device_hash) === String(hash));
  const serialOptions = serialOptionsOf(selectedDevice);
  const hashValueForSelect = hasDeviceHash(hash) ? hash : "";
  const serialValueForSelect = hasSerialValue(serialOptions, serial) ? serial : "";

  // filter logs op klepstoringen
  const klepLogs = deviceactionlogshash
    ?.map((log) => {
      const tube = Object.values(log?.action?.status?.tubeStatus || {});
      const hasError = tube.some((code) => isKlepstoringCode(code));
      return hasError ? log : null;
    })
    .filter(Boolean);

  return (
    <div className={classes.root}>
      <Grid container>
        {isLoading ? (
          <Grid
            style={{ marginTop: 100, marginBottom: 200 }}
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress color="primary" />
            <Typography style={{ marginTop: 50 }}>
              Moment, klepstoringen worden opgehaald...
            </Typography>
          </Grid>
        ) : (
          <Grid item lg={12} md={12} xl={12} xs={12}>
            <Card className={clsx(classes.root)}>
              <CardHeader title="Klepstoringen" subheader="Statuscodes 10 t/m 17" />
              <CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="150">HASH</TableCell>
                      <TableCell>
                        <Select value={hashValueForSelect} onChange={hashChange} fullWidth>
                          <MenuItem disabled value="">
                            <em>Selecteer hash</em>
                          </MenuItem>
                          {devices.map((device, i) => (
                            <MenuItem key={`hash-${i}`} value={device.device_hash}>
                              {device.kenmerk} : {device.device_hash}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={serialValueForSelect}
                          onChange={serialChange}
                          disabled={!selectedDevice}
                          fullWidth
                        >
                          <MenuItem disabled value="">
                            <em>Selecteer serial</em>
                          </MenuItem>
                          {serialOptions.map((s, i) => {
                            const val = s?.serial ?? i;
                            return (
                              <MenuItem key={`serial-${i}`} value={val}>
                                {String(val)}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          id="date"
                          label="Datum"
                          type="date"
                          value={today}
                          className={classes.textField}
                          onChange={dayChange}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {klepLogs.map((log, idx) => (
                      <TableRow key={`${log.device_hash}-${log.eventTime}-${idx}`}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body1">{log.device_hash}</Typography>
                        </TableCell>
                        <TableCell>
                          {moment(log.eventTime).fromNow()}
                          <br />
                          {moment(log.eventTime).format("DD MMMM YYYY, HH:mm:ss")}
                        </TableCell>
                        <TableCell>
                          <ListItem disableGutters>
                            <Grid container justifyContent="center" alignItems="center" spacing={2}>
                              {Object.values(log?.action?.status?.tubeStatus || []).map(
                                (state, i) =>
                                  isKlepstoringCode(state) && (
                                    <Grid
                                      key={`${log.eventTime}-${i}`}
                                      item
                                      xs={4}
                                      sm={4}
                                      md={4}
                                      lg={4}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 6,
                                      }}
                                    >
                                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                                        {i + 1}:
                                      </Typography>
                                      <Icon code={state} />
                                    </Grid>
                                  )
                              )}
                            </Grid>
                          </ListItem>
                        </TableCell>
                        <TableCell>{log?.action?.serial}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Klepstoringen;
